import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FolderKanban, Download, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useProcessosReport, ProcessoRow, ProcessoPaymentStatus } from "@/hooks/useProcessosReport";

type PaymentFilter = ProcessoPaymentStatus | "all";

type SortKey =
  | "contactName"
  | "dealName"
  | "caseTypeName"
  | "caseStatus"
  | "paymentStatus"
  | "totalPending"
  | "overdueAmount"
  | "maxDaysOverdue"
  | "daysSinceClosed";

type SortDir = "asc" | "desc";

const COLUMNS: { key: SortKey; label: string; align?: "right" }[] = [
  { key: "contactName", label: "Contato" },
  { key: "dealName", label: "Deal" },
  { key: "caseTypeName", label: "Tipo" },
  { key: "caseStatus", label: "Status do caso" },
  { key: "paymentStatus", label: "Pagamento" },
  { key: "totalPending", label: "Em aberto", align: "right" },
  { key: "overdueAmount", label: "Atrasado", align: "right" },
  { key: "maxDaysOverdue", label: "Dias atraso", align: "right" },
  { key: "daysSinceClosed", label: "Dias desde fechamento", align: "right" },
];

const PAYMENT_STATUS_META: Record<
  ProcessoPaymentStatus,
  { label: string; color: string; badge: "default" | "secondary" | "destructive" | "outline" }
> = {
  up_to_date: { label: "Em dia", color: "#22c55e", badge: "default" },
  overdue: { label: "Atrasado", color: "#ef4444", badge: "destructive" },
  partial: { label: "Parcial", color: "#f59e0b", badge: "secondary" },
  settled: { label: "Quitado", color: "#3b82f6", badge: "outline" },
  cancelled: { label: "Cancelado", color: "#71717a", badge: "secondary" },
  no_payments: { label: "Sem pagamentos", color: "#cbd5e1", badge: "outline" },
};

const PAYMENT_STATUS_ORDER: ProcessoPaymentStatus[] = [
  "up_to_date",
  "overdue",
  "partial",
  "settled",
  "cancelled",
  "no_payments",
];

const PAYMENT_FILTER_OPTIONS: { value: PaymentFilter; label: string }[] = [
  { value: "all", label: "Todos" },
  ...PAYMENT_STATUS_ORDER.map((s) => ({ value: s, label: PAYMENT_STATUS_META[s].label })),
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

const escapeCSV = (value: unknown) => {
  const str = String(value ?? "");
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

export default function ProcessosReport() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useProcessosReport();

  const [paymentFilter, setPaymentFilter] = useState<PaymentFilter>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const typeOptions = useMemo(() => data?.byType.map((t) => t.type) ?? [], [data]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const filteredRows = useMemo(() => {
    const rows = data?.rows ?? [];
    return rows.filter((row) => {
      if (paymentFilter !== "all" && row.paymentStatus !== paymentFilter) return false;
      if (typeFilter !== "all" && row.caseTypeName !== typeFilter) return false;
      return true;
    });
  }, [data, paymentFilter, typeFilter]);

  const sortedRows = useMemo(() => {
    if (!sortKey) return filteredRows;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      let av: string | number | null;
      let bv: string | number | null;
      if (sortKey === "paymentStatus") {
        av = PAYMENT_STATUS_ORDER.indexOf(a.paymentStatus);
        bv = PAYMENT_STATUS_ORDER.indexOf(b.paymentStatus);
      } else {
        av = a[sortKey];
        bv = b[sortKey];
      }
      // Nulls always sort last, regardless of direction
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv), "pt-BR") * dir;
    });
  }, [filteredRows, sortKey, sortDir]);

  const chartData = useMemo(
    () =>
      (data?.byType ?? []).map((t) => ({
        type: t.type,
        ...PAYMENT_STATUS_ORDER.reduce(
          (acc, s) => ({ ...acc, [s]: t[s] }),
          {} as Record<ProcessoPaymentStatus, number>
        ),
      })),
    [data]
  );

  const handleExportCSV = () => {
    if (!sortedRows.length) return;

    const headers = [
      "Contato",
      "Deal",
      "Tipo",
      "Status do caso",
      "Status pagamento",
      "Valor em aberto",
      "Valor atrasado",
      "Dias em atraso",
      "Data de fechamento",
      "Dias desde o fechamento",
    ];

    const rows = sortedRows.map((row) => [
      row.contactName || "-",
      row.dealName || "-",
      row.caseTypeName,
      row.caseStatus || "-",
      PAYMENT_STATUS_META[row.paymentStatus].label,
      row.totalPending,
      row.overdueAmount,
      row.maxDaysOverdue,
      row.closedDate || "",
      row.daysSinceClosed ?? "",
    ]);

    const csvContent = [
      headers.map(escapeCSV).join(","),
      ...rows.map((r) => r.map(escapeCSV).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "relatorio-processos.csv";
    link.click();
  };

  const summaryCards = useMemo(() => {
    const byStatus = data?.summary.byPaymentStatus;
    return [
      { label: "Total de processos", value: data?.summary.total ?? 0, color: "text-foreground" },
      { label: "Em dia", value: byStatus?.up_to_date ?? 0, color: "text-green-600" },
      { label: "Atrasados", value: byStatus?.overdue ?? 0, color: "text-red-600" },
      { label: "Parcial", value: byStatus?.partial ?? 0, color: "text-amber-600" },
      { label: "Quitados", value: byStatus?.settled ?? 0, color: "text-blue-600" },
      { label: "Cancelados", value: byStatus?.cancelled ?? 0, color: "text-zinc-500" },
      { label: "Sem pagamentos", value: byStatus?.no_payments ?? 0, color: "text-slate-400" },
    ];
  }, [data]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/reports")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <FolderKanban className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">Relatório de Processos</h1>
              <p className="text-muted-foreground">
                Total de processos por tipo e status de pagamento
              </p>
            </div>
          </div>
        </div>
        <Button onClick={handleExportCSV} disabled={!filteredRows.length}>
          <Download className="h-4 w-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      {error && (
        <Card>
          <CardContent className="p-6 text-sm text-destructive">
            Erro ao carregar os processos: {(error as Error).message}
          </CardContent>
        </Card>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {summaryCards.map((card) => (
          <Card key={card.label}>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">{card.label}</p>
              {isLoading ? (
                <Skeleton className="h-8 w-16 mt-1" />
              ) : (
                <p className={`text-3xl font-bold ${card.color}`}>{card.value}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Chart by type */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Processos por tipo</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-72 w-full" />
          ) : chartData.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              Nenhum processo encontrado.
            </p>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="type"
                    tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                    axisLine={false}
                    tickLine={false}
                    width={40}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                    labelStyle={{ color: "hsl(var(--foreground))" }}
                  />
                  <Legend />
                  {PAYMENT_STATUS_ORDER.map((s) => (
                    <Bar
                      key={s}
                      dataKey={s}
                      stackId="a"
                      name={PAYMENT_STATUS_META[s].label}
                      fill={PAYMENT_STATUS_META[s].color}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Status de pagamento</span>
          <Select value={paymentFilter} onValueChange={(v) => setPaymentFilter(v as PaymentFilter)}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAYMENT_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Tipo de processo</span>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              {typeOptions.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto self-end text-sm text-muted-foreground">
          {filteredRows.length} processo(s)
        </div>
      </div>

      {/* Detailed table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {COLUMNS.map((col) => {
                  const active = sortKey === col.key;
                  return (
                    <TableHead
                      key={col.key}
                      className={col.align === "right" ? "text-right" : undefined}
                    >
                      <button
                        type="button"
                        onClick={() => handleSort(col.key)}
                        className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${
                          col.align === "right" ? "flex-row-reverse" : ""
                        } ${active ? "text-foreground font-semibold" : ""}`}
                      >
                        {col.label}
                        {active ? (
                          sortDir === "asc" ? (
                            <ChevronUp className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5" />
                          )
                        ) : (
                          <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />
                        )}
                      </button>
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={COLUMNS.length}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : sortedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} className="text-center text-muted-foreground py-8">
                    Nenhum processo encontrado com os filtros selecionados.
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows.map((row: ProcessoRow) => {
                  const meta = PAYMENT_STATUS_META[row.paymentStatus];
                  return (
                    <TableRow key={row.id}>
                      <TableCell>{row.contactName || "-"}</TableCell>
                      <TableCell>{row.dealName || "-"}</TableCell>
                      <TableCell>{row.caseTypeName}</TableCell>
                      <TableCell>{row.caseStatus || "-"}</TableCell>
                      <TableCell>
                        <Badge variant={meta.badge}>{meta.label}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {row.totalPending > 0 ? formatCurrency(row.totalPending) : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        {row.overdueAmount > 0 ? (
                          <span className="text-red-600">{formatCurrency(row.overdueAmount)}</span>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {row.maxDaysOverdue > 0 ? row.maxDaysOverdue : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        {row.daysSinceClosed ?? "-"}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
